"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Pricing (T10): exact €9.99/month, €24.99/3-months and €79.99/year figures
 * and renewal/cancellation copy ported from main.html (quarterly added
 * 2026-08-27). ADR-014/015: every pilot signup gets free pilot access
 * regardless of the plan chosen — these are the post-pilot prices, made
 * explicit by `pilotNote`. All three CTAs open the embedded AuthPanel
 * sign-up flow (no checkout exists yet in the MVP).
 *
 * ADR-085 IAP-4: with the BUILD-TIME `VITE_BILLING_MODE=app_store` (lib/billingMode.ts; default `whop` = this page unchanged) the lead,
 * the three call-to-action labels and the fine print say what is true once Apple is the payment party: sign-up and the free 14 days stay
 * on the web, a paid plan is chosen in the iPhone app and billed through the Apple Account. The founder's list prices are unchanged.
 *
 * App Review 2026-10-30 (3.1.2(c)) / 2026-11-01: in `app_store` mode the 14 days are "free access" — not a subscription, not a trial of the
 * subscriptions (which have none). So the heading and the three card subtitles also switch to their `*AppStore` keys: prices stand on their
 * own, never "14 days free, then <price>". Whop mode keeps the old keys byte for byte (landing-whop-mode-golden).
 */

import { Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { BILLING_MODE } from "@/lib/billingMode"
import type { AuthMode } from "@/components/auth/authMode"
import { Reveal } from "./Reveal"

interface PricingSectionProps {
  onOpenAuth: (mode: AuthMode) => void
}

export function PricingSection({ onOpenAuth }: PricingSectionProps) {
  const { t } = useI18n()
  const appStore = BILLING_MODE === "app_store"

  const monthlyFeatures = [
    t("landingPage.pricing.monthlyFeature1"),
    t("landingPage.pricing.monthlyFeature2"),
    t("landingPage.pricing.monthlyFeature3"),
    t("landingPage.pricing.monthlyFeature4"),
  ]
  const quarterlyFeatures = [
    t("landingPage.pricing.quarterlyFeature1"),
    t("landingPage.pricing.quarterlyFeature2"),
    t("landingPage.pricing.quarterlyFeature3"),
    t("landingPage.pricing.quarterlyFeature4"),
  ]
  const annualFeatures = [
    t("landingPage.pricing.annualFeature1"),
    t("landingPage.pricing.annualFeature2"),
    t("landingPage.pricing.annualFeature3"),
    t("landingPage.pricing.annualFeature4"),
  ]

  return (
    <section id="pricing" className="scroll-mt-[12vh] border-b border-line bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.pricing.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {appStore ? t("landingPage.pricing.headingAppStore") : t("landingPage.pricing.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">
            {appStore ? t("landingPage.pricing.leadAppStore") : t("landingPage.pricing.lead")}
          </p>

          {/* Craft fix C1 (2026-09-10): this was the only gold element on the
              page and read as a warning rather than an offer. Same copy — the
              pricing and commercial wording is approved and untouched — in the
              brand's quiet notice treatment: paper ground, a green rule, the
              same radius as the pricing cards. */}
          <p
            role="status"
            className="mt-6 w-fit max-w-prose rounded-bb-lg border border-line border-l-4 border-l-green bg-paper px-4 py-2.5 text-sm text-ink-2 shadow-sm"
          >
            {appStore ? t("landingPage.pricing.pilotNoteAppStore") : t("landingPage.pricing.pilotNote")}
          </p>
        </Reveal>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <article className="flex flex-col gap-4 rounded-bb-xl border border-line bg-cream p-8">
            <h3 className="text-lg font-semibold text-ink">
              {t("landingPage.pricing.monthlyTitle")}
            </h3>
            <p className="flex items-baseline gap-1 text-ink">
              <span className="text-3xl font-black">{t("landingPage.pricing.monthlyPrice")}</span>
              <span className="text-sm text-ink-2">
                {t("landingPage.pricing.monthlyPriceSuffix")}
              </span>
            </p>
            <p className="text-sm text-ink-2">{appStore ? t("landingPage.pricing.monthlySubtitleAppStore") : t("landingPage.pricing.monthlySubtitle")}</p>
            <ul className="flex flex-col gap-2">
              {monthlyFeatures.map(feature => (
                <li key={feature} className="flex items-start gap-2 text-sm text-ink-2">
                  <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-green" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button type="button" className="min-h-11" onClick={() => onOpenAuth("signUp")}>
              {appStore ? t("landingPage.pricing.ctaAppStore") : t("landingPage.pricing.monthlyCta")}
            </Button>
            <p className="text-xs text-ink-2">
              {appStore
                ? t("landingPage.pricing.monthlyFineprintAppStore")
                : t("landingPage.pricing.monthlyFineprint")}
            </p>
          </article>

          <article className="flex flex-col gap-4 rounded-bb-xl border border-line bg-cream p-8">
            <h3 className="text-lg font-semibold text-ink">
              {t("landingPage.pricing.quarterlyTitle")}
            </h3>
            <p className="flex items-baseline gap-1 text-ink">
              <span className="text-3xl font-black">{t("landingPage.pricing.quarterlyPrice")}</span>
              <span className="text-sm text-ink-2">
                {t("landingPage.pricing.quarterlyPriceSuffix")}
              </span>
            </p>
            <p className="text-sm text-ink-2">{appStore ? t("landingPage.pricing.quarterlySubtitleAppStore") : t("landingPage.pricing.quarterlySubtitle")}</p>
            <ul className="flex flex-col gap-2">
              {quarterlyFeatures.map(feature => (
                <li key={feature} className="flex items-start gap-2 text-sm text-ink-2">
                  <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-green" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => onOpenAuth("signUp")}
            >
              {appStore ? t("landingPage.pricing.ctaAppStore") : t("landingPage.pricing.quarterlyCta")}
            </Button>
            <p className="text-xs text-ink-2">
              {appStore
                ? t("landingPage.pricing.quarterlyFineprintAppStore")
                : t("landingPage.pricing.quarterlyFineprint")}
            </p>
          </article>

          <article className="flex flex-col gap-4 rounded-bb-xl border-2 border-green bg-ink p-8 text-paper">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{t("landingPage.pricing.annualTitle")}</h3>
              <span className="rounded-bb-full bg-lime px-3 py-1 text-xs font-bold text-ink">
                {t("landingPage.pricing.annualBadge")}
              </span>
            </div>
            <p className="flex items-baseline gap-1">
              <span className="text-3xl font-black">{t("landingPage.pricing.annualPrice")}</span>
              <span className="text-sm text-paper/70">
                {t("landingPage.pricing.annualPriceSuffix")}
              </span>
            </p>
            <p className="text-sm text-paper/80">{appStore ? t("landingPage.pricing.annualSubtitleAppStore") : t("landingPage.pricing.annualSubtitle")}</p>
            <ul className="flex flex-col gap-2">
              {annualFeatures.map(feature => (
                <li key={feature} className="flex items-start gap-2 text-sm text-paper/80">
                  <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-lime" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button
              type="button"
              className="min-h-11 bg-lime text-ink hover:bg-lime/90"
              onClick={() => onOpenAuth("signUp")}
            >
              {appStore ? t("landingPage.pricing.ctaAppStore") : t("landingPage.pricing.annualCta")}
            </Button>
            <p className="text-xs text-paper/70">
              {appStore
                ? t("landingPage.pricing.annualFineprintAppStore")
                : t("landingPage.pricing.annualFineprint")}
            </p>
          </article>
        </div>
      </div>
    </section>
  )
}
